import React from "react";
import Layout from "../component/layout/Layout";
import { Container, Paper, Box, Typography, Divider, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  FileText,
  User,
  BookOpen,
  CreditCard,
  Repeat,
  ShieldCheck,
  Lock,
  Slash,
  Archive,
  AlertTriangle,
  Gavel,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";



// ---------- Styled helpers ----------
const PageWrap = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(8),
  background: "#fff",
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 12,
  boxShadow: "0 8px 30px rgba(7,32,58,0.06)",
  maxWidth: 1100,
  margin: "0 auto",
}));

const SectionHeader = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  alignItems: "center",
}));

const IconCircle = styled("div")(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(135deg, rgba(7,32,58,0.06), rgba(10,30,60,0.02))",
  color: "#07203a",
}));

const SectionBody = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
  color: "rgba(7,32,58,0.88)",
  lineHeight: 1.7,
}));

// map section index to an icon component
const ICONS = [
  FileText,
  User,
  BookOpen,
  CreditCard,
  Repeat,
  ShieldCheck,
  Lock,
  Slash,
  Archive,
  AlertTriangle,
  Gavel,
  Mail,
];

const TermsAndConditions = () => {
  return (
    <Layout tab={2}>
      <PageWrap>
        <Container>
          <Card>
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Terms and Conditions
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "rgba(7,32,58,0.6)", mt: 0.5 }}>
                Effective Date: 13/01/2025
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {termsAndConditions.map((section, index) => {
              // choose icon (fallback to FileText)
              const IconComponent = ICONS[index] ?? FileText;
              return (
                <Box key={index} sx={{ mb: 4 }}>
                  <SectionHeader>
                    <IconCircle aria-hidden>
                      <IconComponent size={20} />
                    </IconCircle>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#07203a" }}>
                      {section.title}
                    </Typography>
                  </SectionHeader>

                  <SectionBody>
                    {/* Render each paragraph exactly as provided */}
                    {section.content.map((para, i) => (
                      <Typography key={i} component="p" sx={{ mb: 1 }}>
                        {para}
                      </Typography>
                    ))}
                  </SectionBody>
                </Box>
              );
            })}

            <Divider sx={{ my: 2 }} />

            {/* Contact block (last section's content is already in the array;
                we render an extra visual contact area for clarity but content is unchanged) */}
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#07203a" }}>
                    Contact
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">Amass skill ventures Pvt Ltd.</Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
                      <Mail size={14} /> <Typography variant="body2">support@skillserve.in</Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
                      <Phone size={14} /> <Typography variant="body2">+91-9220872200</Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
                      <MapPin size={14} />{" "}
                      <Typography variant="body2">Sector 33, Gurugram - 122001</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6} sx={{ textAlign: { xs: "left", md: "right" } }}>
                  <Typography variant="caption" sx={{ color: "rgba(7,32,58,0.6)" }}>
                    Please read these Terms and Conditions carefully. Your use of the Website indicates that you accept
                    these terms.
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Container>
      </PageWrap>
    </Layout>
  );
};

export default TermsAndConditions;


const termsAndConditions = [
  {
    title: "1. General Terms",
    content: [
      '1.1 These Terms and Conditions govern your use of [Your Website Name] ("Website"). By accessing or using our Website, you agree to comply with these terms and conditions, including any amendments made in the future.',
      "1.2 We reserve the right to modify or update these Terms and Conditions at any time. Please review this page periodically to stay informed about any changes.",
    ],
  },
  {
    title: "2. User Accounts",
    content: [
      "2.1 To access certain features on our Website, such as purchasing courses, you may need to create an account. You must provide accurate, complete, and updated information when registering.",
      "2.2 You are responsible for maintaining the confidentiality of your account information and for all activities under your account.",
    ],
  },
  {
    title: "3. Courses and Content",
    content: [
      '3.1 Our Website provides online courses ("Courses") in various subjects. The content of the Courses, including videos, readings, assignments, and quizzes, is intended for personal, non-commercial use.',
      "3.2 All course materials and content are protected by copyright laws. You agree not to copy, distribute, or otherwise use the content for unauthorized purposes.",
    ],
  },
  {
    title: "4. Payment and Pricing",
    content: [
      "4.1 All payments for Courses will be processed through Razorpay, our payment gateway provider. By using Razorpay, you agree to comply with its terms and conditions. Payment details will be securely handled by Razorpay, and we do not store any payment information.",
      "4.2 Prices for Courses are subject to change. We will notify you of any price changes before processing your payment.",
      "4.3 If you encounter any issues with your payment, please contact our support team at [Your Support Email].",
    ],
  },
  {
    title: "5. Refund and Cancellation Policy",
    content: [
      "5.1 We offer a refund policy for Courses under certain conditions. Refund requests must be made within [X] days of purchasing the Course. Please refer to our [Refund Policy] for further details.",
      "5.2 You can cancel your Course access at any time by contacting our support team. Refund eligibility depends on the circumstances outlined in our Refund Policy.",
    ],
  },
  {
    title: "6. Use of Razorpay",
    content: [
      "6.1 Razorpay is integrated with our Website to handle all online payments securely. By using our Website, you acknowledge and agree to Razorpay's terms of service, which can be found at [Razorpay Terms of Service URL].",
      "6.2 [Your Website Name] does not handle or store any credit card details. Payments are processed directly through Razorpay's secure payment gateway.",
    ],
  },
  {
    title: "7. Privacy and Data Protection",
    content: [
      "7.1 We value your privacy. Please refer to our [Privacy Policy] for information on how we collect, use, and protect your personal data.",
      "7.2 By using our Website and purchasing our Courses, you consent to the collection and use of your personal information as described in our Privacy Policy.",
    ],
  },
  {
    title: "8. User Conduct",
    content: [
      "8.1 You agree to use our Website and Courses for lawful purposes only. You will not engage in any activity that may disrupt or damage the Website or the services we offer.",
      "8.2 You agree not to use the Website to upload or distribute content that is harmful, offensive, or infringes upon the rights of others.",
    ],
  },
  {
    title: "9. Intellectual Property",
    content: [
      "9.1 All content on the Website, including Course materials, logos, text, and graphics, is the property of [Your Website Name] or its licensors and is protected by intellectual property laws.",
      "9.2 You may not use any of the Website's content without express written consent from [Your Website Name].",
    ],
  },
  {
    title: "10. Limitation of Liability",
    content: [
      "10.1 To the fullest extent permitted by law, [Your Website Name] and its affiliates are not liable for any indirect, incidental, special, or consequential damages resulting from your use of the Website or Courses.",
      "10.2 We do not guarantee the accuracy or completeness of the Course materials and reserve the right to modify or discontinue any content at any time.",
    ],
  },
  {
    title: "11. Governing Law",
    content: [
      "11.1 These Terms and Conditions are governed by the laws of [Your Country/Region]. Any disputes arising from these terms will be resolved in the appropriate courts in [Your Jurisdiction].",
    ],
  },
  {
    title: "12. Contact Us",
    content: [
      "If you have any questions or concerns regarding these Terms and Conditions, please contact us at:",
      "Amass skill ventures Pvt Ltd.",
      "Email: support@skillserve.in",
      "Phone: +91-9220872200",
      "Address: Sector 33, Gurugram - 122001",
    ],
  },
];
